/**
 * Mode Présentation "Découvrir le Front Uni"
 * -------------------------------------------------
 * Modal plein écran qui joue une présentation cinématographique auto-générée
 * à partir des vraies données du projet (photos, vidéos, communiqués, actions).
 *
 * Enchaîne des scènes avec transitions élégantes :
 *  1. Ouverture logo + slogan
 *  2. Vision (texte sur photo Ken Burns)
 *  3. Nos actions (mosaïque d'événements + effet parallax)
 *  4. Nos productions (vignettes vidéos en séquence)
 *  5. Nos communiqués (défilement typographique cinéma)
 *  6. Chiffres-clés
 *  7. Appel + URL
 *
 * Auto-avance, progress bar, skip disponible, sound toggle.
 */
import { useEffect, useMemo, useRef, useState } from "react";
import {
  X, Play, Pause, SkipForward, Volume2, VolumeX, ArrowRight, Sparkles,
} from "lucide-react";
import Logo from "./Logo";
import api from "../lib/api";

const SCENE_DURATIONS = [4500, 6500, 8500, 7000, 8000, 5000, 5000]; // ms par scène
const SLOGAN = "Unir les forces. Défendre la Nation. Préparer l'avenir.";
const VISION_QUOTE = "Au-delà des États, la société civile congolaise et sa diaspora peuvent peser dans les espaces institutionnels internationaux.";
const VISION_AUTHOR = "Dr Audrey Mundine Kabeya, Présidente";

export default function PresentationMode({ onClose }) {
  const [data, setData] = useState(null);
  const [scene, setScene] = useState(0);
  const [paused, setPaused] = useState(false);
  const [muted, setMuted] = useState(true);
  const timerRef = useRef(null);
  const startedAt = useRef(Date.now());

  useEffect(() => {
    api.get("/webtv/presentation").then((r) => setData(r.data));
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = "";
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  // Auto-avance des scènes
  useEffect(() => {
    if (!data || paused) return;
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(() => {
      if (scene < SCENE_DURATIONS.length - 1) setScene(scene + 1);
      else onClose();
    }, SCENE_DURATIONS[scene]);
    startedAt.current = Date.now();
    return () => { if (timerRef.current) clearTimeout(timerRef.current); };
  }, [scene, data, paused, onClose]);

  const skip = () => setScene((s) => Math.min(SCENE_DURATIONS.length - 1, s + 1));

  if (!data) {
    return (
      <div className="fixed inset-0 z-[100] bg-ink grid place-items-center">
        <div className="text-gold text-center">
          <Sparkles className="w-8 h-8 mx-auto animate-pulse" />
          <p className="mt-3 text-xs uppercase tracking-widest">Préparation de la présentation…</p>
        </div>
      </div>
    );
  }

  const progressPct = ((scene + 1) / SCENE_DURATIONS.length) * 100;

  return (
    <div className="fixed inset-0 z-[100] bg-black text-white overflow-hidden" data-testid="presentation-mode">
      {/* Timeline en haut */}
      <div className="absolute top-0 inset-x-0 z-30 flex gap-1 p-3">
        {SCENE_DURATIONS.map((d, i) => (
          <div key={i} className="flex-1 h-0.5 bg-white/15 rounded-full overflow-hidden">
            <div
              className={`h-full bg-gold ${i < scene ? "w-full" : i === scene ? "animate-fill-bar" : "w-0"}`}
              style={i === scene ? { animationDuration: `${d}ms`, animationPlayState: paused ? "paused" : "running" } : {}}
            />
          </div>
        ))}
      </div>

      {/* Contrôles */}
      <div className="absolute top-8 right-6 z-30 flex items-center gap-3">
        <button onClick={() => setPaused((p) => !p)} className="text-white/85 hover:text-gold" title={paused ? "Reprendre" : "Pause"} data-testid="pres-pause">
          {paused ? <Play className="w-5 h-5" /> : <Pause className="w-5 h-5" />}
        </button>
        <button onClick={() => setMuted((m) => !m)} className="text-white/85 hover:text-gold" title="Son" data-testid="pres-sound">
          {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
        </button>
        <button onClick={skip} className="text-white/85 hover:text-gold" title="Scène suivante" data-testid="pres-skip">
          <SkipForward className="w-5 h-5" />
        </button>
        <button onClick={onClose} className="text-white/85 hover:text-crimson ml-2" title="Fermer" data-testid="pres-close">
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Scène courante */}
      <div className="relative w-full h-full">
        {scene === 0 && <SceneOpening />}
        {scene === 1 && <SceneVision photos={data.photos} />}
        {scene === 2 && <SceneActions actions={data.actions} photos={data.photos} />}
        {scene === 3 && <SceneVideos videos={data.videos} />}
        {scene === 4 && <SceneCommuniques communiques={data.communiques} />}
        {scene === 5 && <SceneStats stats={data.stats} />}
        {scene === 6 && <SceneClosing onClose={onClose} />}
      </div>

      {/* Bandeau du bas */}
      <div className="absolute bottom-4 left-6 right-6 z-30 flex items-center justify-between text-[0.65rem] uppercase tracking-[0.25em] text-white/40">
        <span>Front Uni pour la RDC</span>
        <span>Scène {scene + 1} / {SCENE_DURATIONS.length}</span>
      </div>
    </div>
  );
}

/* ============ SCÈNES ============ */

function SceneOpening() {
  return (
    <div className="w-full h-full grid place-items-center bg-gradient-to-br from-black via-ink to-black relative overflow-hidden">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(212,175,55,0.18),transparent_65%)]" />
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent" />
      <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent" />

      <div className="relative text-center animate-scene-in">
        <div className="grid place-items-center animate-logo-pulse">
          <Logo className="w-40 h-40 sm:w-52 sm:h-52" />
        </div>
        <h1 className="font-serif mt-10 text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight animate-fade-up-delayed">
          <span className="text-white">Front Uni</span>
          <span className="block text-gold italic font-light mt-2">pour la RDC</span>
        </h1>
        <div className="mt-8 max-w-xl mx-auto px-6">
          <div className="h-px bg-gradient-to-r from-transparent via-gold/50 to-transparent" />
          <p className="mt-6 text-white/85 font-serif italic text-lg animate-fade-up-later">
            « {SLOGAN} »
          </p>
        </div>
      </div>
    </div>
  );
}

function SceneVision({ photos }) {
  const bg = photos[0]?.image;
  return (
    <div className="w-full h-full relative bg-black overflow-hidden">
      {bg && (
        <div className="absolute inset-0 animate-ken-burns">
          <img src={bg} alt="" className="w-full h-full object-cover" />
        </div>
      )}
      <div className="absolute inset-0 bg-gradient-to-r from-black via-black/75 to-black/40" />
      <div className="relative h-full grid place-items-center">
        <div className="max-w-4xl px-8 sm:px-16 animate-scene-in">
          <div className="eyebrow eyebrow-on-dark inline-flex items-center gap-3">
            <span className="w-8 h-px bg-gold" /> Notre vision
          </div>
          <h2 className="font-serif text-3xl sm:text-5xl lg:text-6xl mt-6 leading-tight tracking-tight">
            <span className="italic text-gold">« </span>
            <span>{VISION_QUOTE}</span>
            <span className="italic text-gold"> »</span>
          </h2>
          <div className="mt-8 eyebrow eyebrow-on-dark">— {VISION_AUTHOR}</div>
        </div>
      </div>
    </div>
  );
}

function SceneActions({ actions, photos }) {
  const cards = actions.slice(0, 6);
  // Fallback si pas d'événements : on utilise les photos
  const items = cards.length > 0 ? cards : photos.slice(0, 6).map((p) => ({
    title: p.caption || "Action citoyenne",
    image: p.image, city: "", country: "",
  }));

  return (
    <div className="w-full h-full relative bg-black overflow-hidden grid place-items-center px-6 sm:px-16">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_20%_30%,rgba(200,16,46,0.15),transparent_50%)]" />
      <div className="relative max-w-6xl w-full animate-scene-in">
        <div className="eyebrow eyebrow-on-dark inline-flex items-center gap-3">
          <span className="w-8 h-px bg-gold" /> Nos actions & événements
        </div>
        <h2 className="font-serif text-3xl sm:text-5xl mt-4 leading-tight">
          De la parole <span className="text-gold italic font-light">aux actes.</span>
        </h2>
        <div className="mt-10 grid grid-cols-2 sm:grid-cols-3 gap-4">
          {items.slice(0, 6).map((a, i) => (
            <div key={i} className="relative aspect-[4/3] rounded-sm overflow-hidden border border-gold/20 shadow-lg animate-tile-in" style={{ animationDelay: `${i * 120}ms` }}>
              {a.image ? (
                <img src={a.image} alt="" className="w-full h-full object-cover animate-slow-zoom" />
              ) : (
                <div className="w-full h-full bg-gradient-to-br from-ink to-black" />
              )}
              <div className="absolute inset-0 bg-gradient-to-t from-black via-black/40 to-transparent" />
              <div className="absolute inset-x-0 bottom-0 p-3">
                <div className="font-serif text-sm text-white leading-tight line-clamp-2">{a.title}</div>
                {(a.city || a.country) && (
                  <div className="text-[0.65rem] text-gold uppercase tracking-widest mt-1">
                    {[a.city, a.country].filter(Boolean).join(" · ")}
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SceneVideos({ videos }) {
  const list = videos.slice(0, 4);
  return (
    <div className="w-full h-full relative bg-black overflow-hidden grid place-items-center px-6 sm:px-16">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_80%_20%,rgba(212,175,55,0.15),transparent_50%)]" />
      <div className="relative max-w-6xl w-full animate-scene-in">
        <div className="eyebrow eyebrow-on-dark inline-flex items-center gap-3">
          <span className="w-8 h-px bg-gold" /> Nos productions
        </div>
        <h2 className="font-serif text-3xl sm:text-5xl mt-4 leading-tight">
          <span className="text-gold italic font-light">Conférences, </span>interviews, débats.
        </h2>
        {list.length > 0 ? (
          <div className="mt-10 grid grid-cols-2 lg:grid-cols-4 gap-4">
            {list.map((v, i) => (
              <div key={i} className="relative aspect-video rounded-sm overflow-hidden border border-gold/20 shadow-xl animate-tile-in" style={{ animationDelay: `${i * 150}ms` }}>
                {v.thumbnail && <img src={v.thumbnail} alt="" className="w-full h-full object-cover" />}
                <div className="absolute inset-0 bg-black/45 grid place-items-center">
                  <span className="w-14 h-14 rounded-full bg-gold text-ink grid place-items-center animate-pulse-soft">
                    <Play className="w-6 h-6 ml-1" fill="currentColor" />
                  </span>
                </div>
                <div className="absolute inset-x-0 bottom-0 p-3 bg-gradient-to-t from-black to-transparent">
                  <div className="text-[0.65rem] uppercase tracking-widest text-gold">{v.category}</div>
                  <div className="font-serif text-sm text-white leading-tight line-clamp-2 mt-1">{v.title}</div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="mt-10 max-w-2xl">
            <p className="text-white/70 text-lg leading-relaxed">
              Notre Web TV documente chaque prise de parole, chaque conférence, chaque débat.
              Une mémoire vivante de la mobilisation citoyenne congolaise.
            </p>
          </div>
        )}
      </div>
    </div>
  );
}

function SceneCommuniques({ communiques }) {
  const list = communiques.slice(0, 3);
  return (
    <div className="w-full h-full relative bg-gradient-to-br from-ink via-black to-ink overflow-hidden grid place-items-center px-6 sm:px-16">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(200,16,46,0.14),transparent_60%)]" />
      <div className="relative max-w-5xl animate-scene-in">
        <div className="eyebrow inline-flex items-center gap-3 text-crimson">
          <span className="w-8 h-px bg-crimson" /> Communiqués & plaidoyer
        </div>
        <h2 className="font-serif text-3xl sm:text-5xl mt-4 leading-tight">
          <span className="text-white">Nous portons </span>
          <span className="text-gold italic font-light">une voix.</span>
        </h2>
        {list.length > 0 ? (
          <div className="mt-10 space-y-6">
            {list.map((c, i) => (
              <div key={i} className="border-l-2 border-crimson pl-6 animate-slide-fade-in" style={{ animationDelay: `${i * 250}ms` }}>
                <div className="eyebrow text-crimson text-[0.65rem]">{new Date(c.date).toLocaleDateString("fr-FR", { day: "2-digit", month: "long", year: "numeric" })}</div>
                <div className="font-serif text-xl lg:text-2xl text-white mt-2 leading-tight">{c.title}</div>
                <div className="text-white/70 text-sm mt-2 max-w-2xl">{c.excerpt}</div>
              </div>
            ))}
          </div>
        ) : (
          <p className="mt-8 max-w-2xl text-white/70 text-lg">
            Communiqués officiels, positions, prises de parole institutionnelles :
            le Front Uni pour la RDC parle d'une voix claire.
          </p>
        )}
      </div>
    </div>
  );
}

function SceneStats({ stats }) {
  const items = [
    { k: stats.events, l: "Événements & mobilisations" },
    { k: stats.articles, l: "Publications & communiqués" },
    { k: stats.videos, l: "Productions vidéo" },
    { k: "3", l: "Continents mobilisés" },
  ];
  return (
    <div className="w-full h-full relative bg-black overflow-hidden grid place-items-center px-6 sm:px-16">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(212,175,55,0.10),transparent_60%)]" />
      <div className="relative max-w-6xl w-full text-center animate-scene-in">
        <div className="eyebrow eyebrow-on-dark inline-flex items-center gap-3">
          <span className="w-8 h-px bg-gold" /> En chiffres
        </div>
        <h2 className="font-serif text-3xl sm:text-5xl mt-4 leading-tight">
          Une mobilisation <span className="text-gold italic font-light">structurée.</span>
        </h2>
        <div className="mt-14 grid grid-cols-2 lg:grid-cols-4 gap-8">
          {items.map((it, i) => (
            <div key={i} className="animate-fade-up" style={{ animationDelay: `${i * 200}ms` }}>
              <div className="font-serif text-6xl lg:text-7xl text-gold">{it.k}</div>
              <div className="mt-2 h-px bg-gold/30 w-8 mx-auto" />
              <div className="mt-4 text-white/75 text-sm uppercase tracking-widest">{it.l}</div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function SceneClosing({ onClose }) {
  return (
    <div className="w-full h-full relative bg-gradient-to-br from-ink via-black to-ink grid place-items-center">
      <div className="absolute inset-0 bg-[radial-gradient(circle_at_50%_50%,rgba(212,175,55,0.20),transparent_60%)]" />
      <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent" />
      <div className="absolute bottom-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-gold to-transparent" />

      <div className="relative text-center animate-scene-in max-w-3xl px-8">
        <Logo className="w-24 h-24 mx-auto" />
        <h2 className="font-serif mt-8 text-4xl sm:text-6xl leading-[1.05] tracking-tight">
          <span className="text-white">Rejoignez </span>
          <span className="text-gold italic font-light">le mouvement.</span>
        </h2>
        <p className="mt-6 text-white/70 text-lg font-serif italic">
          « {SLOGAN} »
        </p>
        <div className="mt-10 flex flex-col sm:flex-row items-center justify-center gap-4">
          <a href="/adhesion" className="btn-gold" data-testid="pres-cta-join">
            Devenir membre <ArrowRight className="w-4 h-4" />
          </a>
          <button onClick={onClose} className="text-white/75 hover:text-gold text-sm inline-flex items-center gap-2">
            Explorer la plateforme <ArrowRight className="w-3.5 h-3.5" />
          </button>
        </div>
        <div className="mt-10 text-gold text-sm uppercase tracking-[0.3em]">https://frontunirdc.be</div>
      </div>
    </div>
  );
}
